import { getItemsOfLesson } from 'core/crud/lesson.crud';
import { useTranslate } from 'core/hooks/useTranslate';
import { useCourseItems } from 'core/lite/hooks/useCourseItems';
import { useUser } from 'core/lite/hooks/useUser';
import { getTeacherLibraryFoldersService, getTeacherLibraryItemsService } from 'core/services/library.service';
import { getCustomizedBy, getGuidCourseByTemplate } from 'core/utils/course';
import { noShowContentInSmartLink } from 'core/utils/lessonItems';
import { filterNonEvaluableContents, isMintPageReference } from 'core/utils/referenceMaterial';
import { stripHtml, successResponse } from 'core/utils/utils';
import { LESSON_TYPES_IDS } from 'core/variables/constant';
import { useEffect, useState } from 'react';

const FETCH_ALL_PAGE_SIZE = 999;

export function useDialogAddReferenceMaterial({ onClose, onSubmit, lesson, course }) {
  const { courseItems } = useCourseItems(course?.structure_levels === 2 ? course.guid : null);
  const { user } = useUser();
  const { t } = useTranslate();
  const [resources, setResources] = useState([]);
  const [submodules, setSubmodules] = useState([]);
  const [selectedSubmodule, setSelectedSubmodule] = useState(null);
  const [foldersData, setFoldersData] = useState([]);
  const [isLibraryView, setIsLibraryView] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const libraryOption = { label: t('common:My library'), value: 'my-library', isLibrary: true };

    let newSubmodules;
    if (courseItems?.length > 0) {
      newSubmodules = [...filterSubmodules(courseItems), libraryOption];
    } else {
      const lessonOption = {
        label: stripHtml(lesson.lesson_name || lesson.name),
        value: lesson.guid,
        guid: lesson.guid,
      };
      newSubmodules = [lessonOption, libraryOption];
    }
    setSubmodules(newSubmodules);

    if (newSubmodules.length > 0 && !newSubmodules[0].isLibrary) {
      onChangeSubmodule(newSubmodules[0]);
    }
  }, [courseItems, lesson]);

  function filterSubmodules(modules) {
    const unit = modules.find((module) => module.guid === lesson.unitGuid);
    if (!unit) return [];
    return unit.items
      .filter((submodule) => submodule.lesson_type !== LESSON_TYPES_IDS.MINT)
      .map((submodule) => ({
        ...submodule,
        label: stripHtml(submodule.lesson_name),
        value: submodule.lesson_guid || submodule.guid,
      }));
  }

  function normalizeLibraryItem(item) {
    return {
      ...item,
      content_guid: item.contentGuid,
      content_type_guid: item.content?.type,
      content_name: item.name,
    };
  }

  function filterLibraryItems(items) {
    return items
      .map(normalizeLibraryItem)
      .filter((item) => item.content_guid && !noShowContentInSmartLink(item.content_type_guid) && !isMintPageReference(item.content_type_guid));
  }

  async function onChangeSubmodule(submodule) {
    setSelectedSubmodule(submodule);

    if (submodule.isLibrary) {
      setLoading(true);
      setIsLibraryView(true);

      try {
        const [itemsResponse, foldersResponse] = await Promise.all([
          getTeacherLibraryItemsService({ page: 1, page_size: FETCH_ALL_PAGE_SIZE }, user.guid),
          getTeacherLibraryFoldersService({ page: 1, page_size: FETCH_ALL_PAGE_SIZE }, user.guid),
        ]);

        const looseItems = itemsResponse?.data ? filterLibraryItems(itemsResponse.data) : [];

        const folders = foldersResponse?.data
          ? foldersResponse.data.map((folder) => ({
              ...folder,
              items: filterLibraryItems(folder.items || []),
            }))
          : [];

        setResources(looseItems);
        setFoldersData(folders);
      } catch (error) {
        console.error('Error getting library items', error);
        setResources([]);
        setFoldersData([]);
      } finally {
        setLoading(false);
      }
    } else {
      setIsLibraryView(false);
      setFoldersData([]);
      setLoading(true);

      try {
        const response = await getItemsOfLesson(
          {
            guid: submodule.lesson_guid || submodule.guid,
            data: { pageSize: '', offset: 0, fromDate: '', toDate: '', period: 'course' },
            customized: getCustomizedBy(course, user),
          },
          getGuidCourseByTemplate(course)
        );
        if (successResponse(response)) {
          setResources(
            filterNonEvaluableContents(response.data.data.items)
              .filter((item) => !isMintPageReference(item.content_type_guid) || (item.content_guid && (item.lesson_item_guid || item.guid)))
              .map((item) => ({
                ...item,
                lesson_item_guid: isMintPageReference(item.content_type_guid) ? item.lesson_item_guid || item.guid : undefined,
                lesson_guid: submodule.lesson_guid || submodule.guid,
              }))
          );
        } else {
          console.error('Error getting items of lesson', response.data.error);
          setResources([]);
        }
      } catch (error) {
        console.error('Error getting items of lesson', error);
        setResources([]);
      } finally {
        setLoading(false);
      }
    }
  }

  function closeDialog() {
    onClose();
  }

  function submitDialog(resource) {
    onSubmit(resource);
  }

  return { t, resources, submodules, selectedSubmodule, onChangeSubmodule, closeDialog, submitDialog, foldersData, isLibraryView, loading };
}
